/**
 * Phone number input with country selector.
 * Uses libphonenumber-js for formatting/validation and outputs E.164 values.
 * Composed from existing shadcn/ui primitives (Button, Command, Input, Popover).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Country helpers
// ---------------------------------------------------------------------------

/** Display name for a country code using the browser's Intl API. */
const getCountryName = (code: CountryCode, locale = 'en'): string => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

/** Emoji flag from a two-letter country code (regional indicator symbols). */
const getFlagEmoji = (code: CountryCode): string =>
  code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');

/** Placeholder derived from the example number for a country. */
const getPlaceholder = (country: CountryCode): string => {
  const example = getExampleNumber(country, examples);
  return example?.formatNational() ?? '';
};

// Pre-compute the sorted country list once.
const COUNTRY_LIST: Array<{ code: CountryCode; name: string; callingCode: string }> = getCountries()
  .map((code) => ({ code, name: getCountryName(code), callingCode: getCountryCallingCode(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));

// ---------------------------------------------------------------------------
// PhoneInput
// ---------------------------------------------------------------------------

export interface PhoneInputProps extends Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'size'
> {
  /** E.164 value (e.g. "+15551234567") */
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: CountryCode;
}

/**
 * Phone input with integrated country code selector.
 * Outputs E.164 formatted phone numbers via `onChange`.
 */
const PhoneInput = ({
  value = '',
  onChange,
  onBlur,
  disabled,
  defaultCountry = 'US',
  className,
  placeholder,
  ref,
  ...rest
}: PhoneInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const { t, i18n } = useTranslation();

  // Detect country from existing value, fall back to defaultCountry
  const detectCountry = useCallback(
    (phone: string): CountryCode => {
      if (!phone) {
        return defaultCountry;
      }
      const parsed = parsePhoneNumberFromString(phone);
      return (parsed?.country as CountryCode) ?? defaultCountry;
    },
    [defaultCountry]
  );

  const [country, setCountry] = useState<CountryCode>(() => detectCountry(value));
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Sync country selector when value changes externally (e.g. form reset).
  // Only update when parsing succeeds — partial numbers during typing
  // would fall back to defaultCountry and flip the flag unexpectedly.
  useEffect(() => {
    if (!value) {
      return;
    }
    const parsed = parsePhoneNumberFromString(value);
    if (parsed?.country) {
      setCountry((prev) =>
        prev !== (parsed.country as CountryCode) ? (parsed.country as CountryCode) : prev
      );
    }
  }, [value]);

  // Format the E.164 value as a national number for display
  const displayValue = useMemo(() => {
    if (!value) {
      return '';
    }
    const parsed = parsePhoneNumberFromString(value);
    if (parsed && parsed.country === country) {
      return parsed.formatNational();
    }
    // If the value doesn't match the selected country, show raw digits after calling code
    const callingCode = getCountryCallingCode(country);
    const raw = value.replace(/^\+/, '');
    if (raw.startsWith(callingCode)) {
      return raw.slice(callingCode.length);
    }
    return raw;
  }, [value, country]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      // "00" is the IDD prefix used in most of Europe/Asia and is interchangeable with "+"
      // in international format. Treat such input as international so libphonenumber-js
      // picks up the right country (e.g. 0030… → +30…, switching the flag to Greece).
      const isInternational = input.startsWith('+') || /^00\d/.test(input);
      const normalized = input.startsWith('00') ? `+${input.slice(2)}` : input;
      const formatter = new AsYouType(isInternational ? undefined : country);
      formatter.input(normalized);
      const number = formatter.getNumber();
      onChange?.(number?.number?.toString() ?? '');
    },
    [country, onChange]
  );

  const handleCountrySelect = useCallback(
    (newCountry: CountryCode) => {
      setCountry(newCountry);
      setPopoverOpen(false);

      // Re-parse current value with new country, or reset if empty
      if (value) {
        const parsed = parsePhoneNumberFromString(value);
        if (parsed) {
          // Keep the national number, change the country
          const national = parsed.nationalNumber;
          const formatter = new AsYouType(newCountry);
          formatter.input(national);
          const number = formatter.getNumber();
          onChange?.(number?.number?.toString() ?? '');
          return;
        }
      }
      onChange?.('');
    },
    [value, onChange]
  );

  const placeholderValue = useMemo(() => getPlaceholder(country), [country]);

  const countryList = useMemo(
    () =>
      COUNTRY_LIST.map((c) => ({ ...c, name: getCountryName(c.code, i18n.language) })).sort(
        (a, b) => a.name.localeCompare(b.name)
      ),
    [i18n.language]
  );

  return (
    <div className={cn('flex', className)}>
      <Popover modal open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline-neutral"
            className="flex h-10 items-center gap-1 rounded-r-none border-r-0 px-2 focus-visible:z-10"
            aria-label={t('common.select_country')}
            disabled={disabled}
          >
            <span className="text-base leading-none">{getFlagEmoji(country)}</span>
            <span className="text-xs text-muted-foreground">+{getCountryCallingCode(country)}</span>
            <ChevronDownIcon className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-1300 w-72 p-0" align="start">
          <Command>
            <CommandInput placeholder={t('common.search')} />
            <CommandList>
              <CommandEmpty>{t('common.no_results')}</CommandEmpty>
              <CommandGroup>
                {countryList.map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} +${c.callingCode}`}
                    onSelect={() => handleCountrySelect(c.code)}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4 text-primary',
                        country === c.code ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="mr-2 text-base">{getFlagEmoji(c.code)}</span>
                    <span className="flex-1 truncate text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground">+{c.callingCode}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        ref={ref}
        type="tel"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder ?? placeholderValue}
        className="rounded-l-none"
        {...rest}
      />
    </div>
  );
};

export { PhoneInput };
